import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import SearchInput from "src/components/widgets/SearchInput";
import { cn, getCustomizationValue, renderCurrency, showInfoToast, showSuccessToast } from "src/lib/utils";
import CustomDataTable from "src/components/CustomDataTable";
import CustomDropdown from "src/components/CustomDropdown";
import { Button, buttonVariants } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { archiveInvoice, getInvoices, markInvoiceAsPaid } from "src/api/invoice";
import { initiatePayment, verifyPayment } from "src/api/payments";
import Monnify from "monnify-js";
import { cva } from "class-variance-authority";
import env from "src/config/env";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { useAuth } from "src/providers/AuthProvider";
import { useDownloadInvoiceMutation } from "src/api/mutations";

const monnify = new Monnify(env.REACT_APP_MONNIFY_API_KEY, env.REACT_APP_MONNIFY_CONTRACT_CODE);

const statusVariants = cva("tw-capitalize tw-font-semibold", {
	variants: {
		status: {
			pending: "tw-text-orange",
			partial: "tw-text-orange",
			paid: "tw-text-green",
		},
	},
	defaultVariants: {
		status: "default",
	},
});

const columns = (navigate, archiveMutate, initiatePaymentMutate, markInvoiceAsPaidMutate, business_uuid, user, downloadInvoiceMutate) => [
	{
		name: cn(getCustomizationValue(user, "invoice_label", true), " No"),
		selector: (row) => row.invoice_id,
	},
	{
		name: cn(getCustomizationValue(user, "customer_label", true), "Name"),
		selector: (row) => `${row.first_name} ${row.last_name}`,
		grow: 1.5,
	},
	{
		name: "Balance",
		selector: (row) => renderCurrency(row.balance),
	},
	{
		name: "Total Amount",
		selector: (row) => renderCurrency(row.total_amount),
	},
	{
		name: "Due Date",
		selector: (row) => format(new Date(row.due_date), "MMM dd, yyyy"),
	},
	{
		name: "Status",
		cell: (row) => <span className={statusVariants({ status: row.status })}>{row.status}</span>,
	},
	{
		name: "Action",
		cell: (row) => (
			<CustomDropdown
				trigger={
					<Button variant="hover">
						<EllipsisIcon />
					</Button>
				}
				actions={[
					{ label: cn("View ", getCustomizationValue(user, "invoice_label", true)), onClick: () => navigate(`/invoices/view/${row.uuid}`) },
					row?.payment_method === "transfer" &&
						row.status !== "paid" && [
							{
								label: (
									<CustomConfirmDialog
										trigger={<span>Initiate Payment</span>}
										mutateFn={() =>
											initiatePaymentMutate({
												customer_id: row.customer_id,
												invoice_id: row.id,
												business_uuid,
											})
										}
									/>
								),
								asChild: true,
							},
						],
					row?.payment_method === "offline" &&
						row.status !== "paid" && [
							{
								label: (
									<CustomConfirmDialog
										trigger={<span>Mark as paid</span>}
										mutateFn={() => markInvoiceAsPaidMutate({ uuid: row.uuid })}
									/>
								),
								asChild: true,
							},
						],
					{
						label: (
							<CustomConfirmDialog
								trigger={<span>Archive {getCustomizationValue(user, "invoice_label", true)}</span>}
								mutateFn={() => archiveMutate({ uuid: row.uuid })}
							/>
						),
						asChild: true,
					},
					row.status === "paid" && [
						{
							label: "Download Invoice",
							onClick: () => downloadInvoiceMutate(row.uuid),
						},
					],
				]
					.filter(Boolean)
					.flat()}
			/>
		),
		width: "100px",
	},
];

const Invoices = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const { data, isLoading, refetch } = useQuery({
		queryKey: ["invoices", page, limit, search],
		queryFn: () => getInvoices({ page, limit, search }),
	});
	const { mutate: archiveMutate } = useMutation(archiveInvoice, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			refetch();
		},
	});
	const { mutate: markInvoiceAsPaidMutate } = useMutation(markInvoiceAsPaid, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			refetch();
		},
	});
	const { mutate: verifyMutate } = useMutation(verifyPayment, {
		onSuccess: () => refetch(),
	});
	const { mutate: initiateMutate } = useMutation(initiatePayment, {
		onSuccess: (data) => {
			monnify.initializePayment({
				...data,
				onClose: (response) => {
					if (response.status === "SUCCESS") {
						refetch();
						verifyMutate({ reference: response.paymentReference, business_uuid: user?.business_uuid });
					}
				},
			});
		},
	});
	const { mutate: downloadInvoiceMutate } = useDownloadInvoiceMutation();

	const invoices = data?.data?.data;
	const totalRow = data?.data?.total;

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop">
					<h2>{getCustomizationValue(user, "invoice_label")}</h2>
				</div>
				<div className="innerContent">
					<ActionBar />

					<CustomDataTable
						columns={columns(
							navigate,
							archiveMutate,
							initiateMutate,
							markInvoiceAsPaidMutate,
							user?.business_uuid,
							user,
							downloadInvoiceMutate,
						)}
						data={invoices}
						progressPending={isLoading}
						paginationTotalRows={totalRow}
					/>
				</div>
			</div>
		</div>
	);
};

const ActionBar = () => {
	const { user } = useAuth();
	const navigate = useNavigate();

	return (
		<div className="inner-top tw-flex-wrap tw-gap-3">
			<div className="inner-top-right tw-flex-wrap tw-gap-3 tw-ml-auto">
				<SearchInput name="search" placeholder="Search..." />
				<button
					onClick={() => {
						if (!user?.bvn_verified) {
							showInfoToast("Please verify your BVN before proceeding");
							return navigate("/account");
						}
						navigate("/invoices/add");
					}}
					className={buttonVariants({ variant: "secondary" })}
				>
					Add New {getCustomizationValue(user, "invoice_label", true)}
				</button>

				<Link to="/invoices/archived" className={buttonVariants({ className: "!tw-bg-orange" })}>
					View Archived
				</Link>
			</div>
		</div>
	);
};

const Comp = withPermissionWrapper(Invoices, "view_invoices");
export default Comp;
